import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, InlineNotification, Tag } from '@carbon/react';
import { Link } from 'react-router';
import { api } from '../api/client';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';

type Check = {
  key: string; label: string; status: 'ok' | 'firing'; detail: string; oneShot: boolean;
  since: string | null; checkedAt: string; notified: string; lastSentAt: string | null; lastError: string | null;
};
type Drill = { ok: boolean; tables: number; rows: number; restore_ms: number };
type Data = {
  telegram: { chatId: string; hasToken: boolean; on: boolean };
  checks: Check[];
  backups: null | {
    error?: string;
    last: { ok: boolean; at: string; file?: string; bytes?: number; error?: string } | null;
    offbox: { ok?: boolean; at: string; file?: string; count?: number; error?: string; drill?: Drill } | null;
    files: { name: string; bytes: number; at: string }[];
  };
};

const mb = (b: number) => (b / 1e6).toLocaleString('vi-VN', { maximumFractionDigits: 2 }) + ' MB';

function StatusTag({ c }: { c: Check }) {
  if (c.status === 'firing') return <Tag type="red" size="sm">{c.oneShot ? 'Thất bại' : 'Có sự cố'}</Tag>;
  if (c.since) return <Tag type="warm-gray" size="sm">Đang theo dõi</Tag>;
  return <Tag type="green" size="sm">Bình thường</Tag>;
}

function Delivery({ c, on }: { c: Check; on: boolean }) {
  const owed = (c.status === 'firing' && c.notified !== 'firing') || (c.status === 'ok' && c.notified === 'firing');
  if (c.lastError && owed) return <span className="text-error">Gửi lỗi: {c.lastError}</span>;
  if (owed) return <span className="muted">{on ? 'Đang gửi' : 'Chờ bật Telegram'}</span>;
  return c.lastSentAt ? <span className="muted">Đã báo <When v={c.lastSentAt} /></span> : <span className="muted">-</span>;
}

export function OpsPage() {
  const q = useQuery({ queryKey: ['ops'], queryFn: () => api.get<Data>('/api/admin/ops'), refetchInterval: 15_000 });
  const test = useMutation({ mutationFn: () => api.post('/api/admin/ops/telegram-test', {}) });
  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { telegram: tg, checks, backups } = q.data;
  const watched = checks.filter((c) => !c.oneShot);
  const firing = watched.filter((c) => c.status === 'firing').length;
  return (
    <div className="page">
      <PageHeader title="Vận hành"
        lead="Hệ A, hệ B, worker, ổ đĩa, chứng chỉ HTTPS và sao lưu được kiểm tra mỗi 30 giây. Khi có sự cố và khi hết sự cố, CMS nhắn nhóm Telegram quản trị một lần; sự cố còn kéo dài được nhắc lại theo Cài đặt." />

      <section className="section">
        <h2>Nhóm Telegram quản trị</h2>
        <div className="stack" style={{ maxWidth: 640 }}>
          <div className="row">
            {tg.on ? <Tag type="green" size="sm">Đang bật</Tag> : <Tag type="warm-gray" size="sm">Chưa bật</Tag>}
            <span className="muted">
              Mã nhóm: {tg.chatId || 'chưa nhập'} · Token bot trên box: {tg.hasToken ? 'có' : 'chưa có'}
            </span>
          </div>
          {!tg.on ? (
            <p className="muted">
              Cần cả hai: token bot trong tệp <code>/opt/rikserver/data/telegram_bot_token</code> trên box (cùng bot với quên mật khẩu)
              và mã nhóm trong <Link to="/settings">Cài đặt</Link>. Khi chưa bật, sự cố vẫn hiện ở đây và được gửi ngay khi bật.
            </p>
          ) : null}
          <div className="row">
            <Button size="sm" kind="tertiary" disabled={test.isPending} onClick={() => test.mutate()}>Gửi tin thử</Button>
            {test.isSuccess ? <span className="muted">Đã gửi. Kiểm tra nhóm Telegram.</span> : null}
          </div>
          <InlineError error={test.error} />
        </div>
      </section>

      <section className="section section--flush" style={{ marginTop: 32 }}>
        <h2>Tình trạng {firing ? <Tag type="red" size="sm">{firing} sự cố</Tag> : null}</h2>
        {watched.length ? (
          <table className="plain">
            <thead><tr><th>Thành phần</th><th>Trạng thái</th><th>Chi tiết</th><th>Từ lúc</th><th>Báo Telegram</th></tr></thead>
            <tbody>
              {watched.map((c) => (
                <tr key={c.key}>
                  <td className="is-title">{c.label}</td>
                  <td data-label="Trạng thái"><StatusTag c={c} /></td>
                  <td data-label="Chi tiết">{c.detail}</td>
                  <td data-label="Từ lúc">{c.since ? <When v={c.since} /> : <span className="muted">-</span>}</td>
                  <td data-label="Báo Telegram"><Delivery c={c} on={tg.on} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState title="Chưa có lần kiểm tra nào" body="API và worker kiểm tra lần đầu trong vòng 30 giây sau khi chạy." />}
      </section>

      <section className="section">
        <h2>Sao lưu dữ liệu CMS</h2>
        {!backups ? <p className="muted">Máy này không cấu hình thư mục sao lưu (chỉ box có).</p> : backups.error ? (
          <InlineNotification kind="error" lowContrast hideCloseButton title="Không đọc được thư mục sao lưu" subtitle={backups.error} />
        ) : (
          <div className="stack">
            <div className="row">
              Lần chạy gần nhất: {backups.last ? <><When v={backups.last.at} /> {backups.last.ok ? <Tag type="green" size="sm">Thành công</Tag> : <Tag type="red" size="sm">Lỗi</Tag>}</> : 'chưa chạy'}
              {backups.last && !backups.last.ok ? <span className="text-error"> {backups.last.error}</span> : null}
            </div>
            <div className="row">
              Bản sao trên máy Mac: {backups.offbox ? (backups.offbox.ok === false
                ? <>lần kéo lúc <When v={backups.offbox.at} /> <Tag type="red" size="sm">Lỗi</Tag> <span className="text-error">{backups.offbox.error}</span></>
                : <>{backups.offbox.count} bản, kéo lần cuối lúc <When v={backups.offbox.at} />
                  {backups.offbox.drill ? <span className="muted">; thử khôi phục {backups.offbox.drill.tables} bảng, {backups.offbox.drill.rows.toLocaleString('vi-VN')} dòng, khớp manifest</span> : null}</>) : 'chưa kéo lần nào'}
            </div>
            {backups.files.length ? (
              <table className="plain">
                <thead><tr><th>Bản sao lưu</th><th>Lúc</th><th className="r">Dung lượng</th></tr></thead>
                <tbody>
                  {backups.files.map((f) => (
                    <tr key={f.name}><td className="num">{f.name}</td><td data-label="Lúc"><When v={f.at} /></td><td className="r" data-label="Dung lượng">{mb(f.bytes)}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState title="Chưa có bản sao lưu" body="Box sao lưu mỗi ngày lúc 03:00." />}
          </div>
        )}
      </section>

    </div>
  );
}
